/**
 * AdminMfaGate — Two-factor authentication gate for the CCN admin panel.
 *
 * Rendered by AdminLogin after email/password succeed and the backend
 * returns `status: "mfa_setup_required"` or `status: "mfa_required"`.
 *
 * Two modes:
 *   • Setup    — first-time enrollment: shows QR + textual secret, asks
 *                for the 6-digit code, calls enroll → challenge → verify.
 *   • Verify   — an existing verified TOTP factor is on file: shows only
 *                the code input, calls challenge → verify.
 *
 * On success, the backend sets the `ccn_session` cookie (aal2 JWT). We
 * then reload the app so AuthContext picks up the new session.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { authApi } from "../../lib/api";
import { Loader2, ShieldCheck, KeyRound, Copy, CheckCircle2, LogOut, RefreshCw } from "lucide-react";

const CODE_LEN = 6;

const AdminMfaGate = ({ initialStatus, onCancel }) => {
  // "setup" | "verify"
  const [mode, setMode] = useState(initialStatus === "mfa_setup_required" ? "setup" : "verify");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [code, setCode] = useState("");
  const [factorId, setFactorId] = useState(null);
  const [qrSvg, setQrSvg] = useState("");
  const [secret, setSecret] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const codeInputRef = useRef(null);

  const startFlow = async () => {
    setLoading(true); setError(null); setCode("");
    try {
      // Confirm current backend view of the MFA state (may differ from initialStatus).
      // NOTE: we do NOT create a challenge here anymore — per Supabase docs, the
      // challenge must be created immediately before each verify() call in the
      // same submit handler to avoid staleness across TOTP windows and to avoid
      // any accidental reuse of a challenge_id across attempts.
      const st = await authApi.mfa.status();
      const status = st.data?.status;
      if (status === "mfa_required") {
        setMode("verify");
        setFactorId(st.data.factor_id);
      } else {
        setMode("setup");
        const enr = await authApi.mfa.enroll();
        setFactorId(enr.data.factor_id);
        setQrSvg(enr.data.qr_code_svg || "");
        setSecret(enr.data.secret || "");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Impossibile avviare la verifica MFA. Riprova.");
    } finally {
      setLoading(false);
      // Autofocus the code input once ready
      setTimeout(() => codeInputRef.current?.focus(), 50);
    }
  };

  useEffect(() => { startFlow(); }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError("Inserisci un codice numerico a 6 cifre.");
      return;
    }
    if (!factorId) {
      setError("Sessione MFA non pronta. Ricarica la pagina e riprova.");
      return;
    }
    setSubmitting(true);
    try {
      // Fresh challenge → immediate verify, in the SAME submit. This is the
      // sequence mandated by Supabase MFA docs. Never reuse a challenge_id.
      const chr = await authApi.mfa.challenge(factorId);
      const freshChallengeId = chr.data?.challenge_id;
      if (!freshChallengeId) {
        throw new Error("challenge_id mancante");
      }
      await authApi.mfa.verify(factorId, freshChallengeId, code);
      // Full admin session (aal2 cookie) is now set by the backend.
      // Force a full reload so AuthContext refetches /auth/me.
      window.location.assign("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Codice non valido.");
      setCode("");
      setTimeout(() => codeInputRef.current?.focus(), 50);
      // No preemptive re-challenge here: the next submit will create its own
      // fresh challenge, keeping challenge_id single-use and never stale.
    } finally {
      setSubmitting(false);
    }
  };

  const cancelToLogin = async () => {
    try { await authApi.logout(); } catch (_) { /* ignore */ }
    onCancel?.();
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch (_) { /* clipboard not available */ }
  };

  const title = mode === "setup" ? "Proteggi il tuo account amministratore" : "Verifica in due passaggi";
  const subtitle = mode === "setup"
    ? "Scansiona il QR code con Google Authenticator, Microsoft Authenticator, Authy, 1Password o Password di Apple, poi inserisci il codice a 6 cifre."
    : "Inserisci il codice a 6 cifre generato dalla tua app di autenticazione.";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#F8F9FA]">
      <div className="w-full max-w-md" data-testid="admin-mfa-gate">
        <div className="inline-flex items-center gap-3 mb-10">
          <div className="w-11 h-11 rounded-2xl bg-[#12964A] flex items-center justify-center text-white">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-lg text-[#1F2937]">CCN Sanluri</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Verifica sicurezza</div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-8 md:p-10 card-shadow">
          <h1 className="font-display text-3xl font-bold text-[#1F2937] mb-2">{title}</h1>
          <p className="text-slate-500 mb-8 text-sm leading-relaxed">{subtitle}</p>

          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Preparazione…
            </div>
          )}

          {!loading && mode === "setup" && qrSvg && (
            <div className="mb-6 space-y-4">
              <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4 flex justify-center">
                <div
                  className="w-48 h-48"
                  data-testid="admin-mfa-qr"
                  // Supabase returns a full SVG document. Injecting server-side
                  // controlled markup: acceptable because the SVG payload is
                  // generated by Supabase, not by user content.
                  dangerouslySetInnerHTML={{ __html: qrSvg }}
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Chiave testuale (alternativa)</div>
                <div className="flex items-center gap-2 rounded-xl border border-[#E5E7EB] bg-[#F8F9FA] px-3 py-2">
                  <code data-testid="admin-mfa-secret" className="text-xs font-mono text-slate-700 flex-1 break-all">{secret}</code>
                  <button
                    type="button" onClick={copySecret}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#12964A] whitespace-nowrap"
                    aria-label="Copia chiave"
                  >
                    {copiedSecret ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copiata</> : <><Copy className="w-3.5 h-3.5" /> Copia</>}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!loading && (
            <form onSubmit={submit} className="space-y-5">
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="admin-mfa-error">{error}</div>
              )}
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Codice a 6 cifre</label>
                <div className="flex items-center gap-3 bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl px-4">
                  <KeyRound className="w-4 h-4 text-slate-400" />
                  <input
                    ref={codeInputRef}
                    data-testid="admin-mfa-code-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={CODE_LEN}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D+/g, "").slice(0, CODE_LEN))}
                    placeholder="000000"
                    required
                    className="w-full bg-transparent focus:outline-none py-3.5 font-mono tracking-[0.4em] text-center text-lg"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={submitting || code.length !== CODE_LEN}
                data-testid="admin-mfa-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-2xl py-4 font-medium transition-colors"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {submitting ? "Verifica in corso…" : (mode === "setup" ? "Attiva e accedi" : "Verifica e accedi")}
              </button>
            </form>
          )}

          <div className="mt-6 flex items-center justify-between text-sm">
            <button type="button" onClick={cancelToLogin} data-testid="admin-mfa-cancel"
                    className="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#12964A]">
              <LogOut className="w-3.5 h-3.5" /> Annulla e torna al login
            </button>
            {mode === "setup" && !loading && (
              <button type="button" onClick={startFlow}
                      className="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#12964A]">
                <RefreshCw className="w-3.5 h-3.5" /> Rigenera QR
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          La verifica in due passaggi è obbligatoria per l&apos;accesso al pannello amministratore.
        </p>
      </div>
    </div>
  );
};

export default AdminMfaGate;
